import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { 
  Wallet, Users, Send, Upload, Clock, CheckCircle, XCircle, 
  AlertCircle, Search, Plus, Trash2, Calendar, TrendingUp, CreditCard
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const PaymentDashboard = () => {
  const [users, setUsers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [rules, setRules] = useState([]);
  const [banks, setBanks] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('users');
  
  // Dialogs
  const [showManualPayment, setShowManualPayment] = useState(false);
  const [showAddBalance, setShowAddBalance] = useState(false);
  const [showCreateRule, setShowCreateRule] = useState(false);
  const [showBankDetails, setShowBankDetails] = useState(false);
  
  // Selected user for actions
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Form states
  const [manualPaymentAmount, setManualPaymentAmount] = useState('');
  const [manualPaymentNotes, setManualPaymentNotes] = useState('');
  const [addBalanceAmount, setAddBalanceAmount] = useState('');
  const [addBalanceDescription, setAddBalanceDescription] = useState('');
  
  // Bank details form
  const [bankForm, setBankForm] = useState({
    bank_name: '',
    branch_code: '',
    account_number: '',
    account_holder_name: '',
    account_type: 'savings'
  });
  
  // Rule form
  const [ruleType, setRuleType] = useState('threshold');
  const [thresholdAmount, setThresholdAmount] = useState('');
  const [monthlyAmount, setMonthlyAmount] = useState('');
  const [monthlyDay, setMonthlyDay] = useState('1');
  const [ruleStartDate, setRuleStartDate] = useState('');
  const [ruleEndDate, setRuleEndDate] = useState('');
  
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchData();
    fetchBanks();
  }, []);

  const fetchBanks = async () => {
    try {
      const response = await axios.get(`${API}/api/banks`);
      setBanks(response.data);
    } catch (error) {
      console.error('Could not fetch banks');
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersRes, transactionsRes, rulesRes] = await Promise.all([
        axios.get(`${API}/api/admin/payments/users`),
        axios.get(`${API}/api/admin/payments/transactions`),
        axios.get(`${API}/api/admin/payments/rules`)
      ]);
      setUsers(usersRes.data);
      setTransactions(transactionsRes.data);
      setRules(rulesRes.data);
    } catch (error) {
      toast.error('Kon nie data laai nie');
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter(user =>
    user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleManualPayment = async () => {
    if (!selectedUser || !manualPaymentAmount) return;
    
    setProcessing(true);
    try {
      const formData = new FormData();
      formData.append('user_id', selectedUser.id);
      formData.append('amount', manualPaymentAmount);
      formData.append('notes', manualPaymentNotes);
      
      const response = await axios.post(`${API}/api/admin/payments/manual`, formData);
      
      // Show appropriate message based on status
      if (response.data.status === 'pending_manual') {
        // Show detailed info for manual payments
        const bankDetails = response.data.bank_details;
        if (bankDetails) {
          toast.success(
            `Betaling geskep (R${manualPaymentAmount}). EFT na: ${bankDetails.bank_name} - ${bankDetails.account_number} (${bankDetails.account_holder_name})`,
            { duration: 8000 }
          );
        } else {
          toast.success(response.data.message, { duration: 5000 });
        }
      } else {
        toast.success(response.data.message);
      }
      
      setShowManualPayment(false);
      setManualPaymentAmount('');
      setManualPaymentNotes('');
      setSelectedUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Betaling het gefaal');
    } finally {
      setProcessing(false);
    }
  };

  const handleAddBalance = async () => {
    if (!selectedUser || !addBalanceAmount) return;
    
    setProcessing(true);
    try {
      const formData = new FormData();
      formData.append('user_id', selectedUser.id);
      formData.append('amount', addBalanceAmount);
      formData.append('description', addBalanceDescription);
      
      await axios.post(`${API}/api/admin/payments/add-balance`, formData);
      toast.success('Balans bygevoeg');
      setShowAddBalance(false);
      setAddBalanceAmount('');
      setAddBalanceDescription('');
      setSelectedUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kon nie balans byvoeg nie');
    } finally {
      setProcessing(false);
    }
  };

  const handleCreateRule = async () => {
    if (!selectedUser) return;
    
    setProcessing(true);
    try {
      const formData = new FormData();
      formData.append('user_id', selectedUser.id);
      formData.append('rule_type', ruleType);
      
      if (ruleType === 'threshold') {
        formData.append('threshold_amount', thresholdAmount);
      } else {
        formData.append('monthly_amount', monthlyAmount);
        formData.append('monthly_day', monthlyDay);
        if (ruleStartDate) formData.append('start_date', ruleStartDate);
        if (ruleEndDate) formData.append('end_date', ruleEndDate);
      }
      
      await axios.post(`${API}/api/admin/payments/rules`, formData);
      toast.success('Reël geskep');
      setShowCreateRule(false);
      resetRuleForm();
      setSelectedUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kon nie reël skep nie');
    } finally {
      setProcessing(false);
    }
  };

  const handleDeleteRule = async (ruleId) => {
    try {
      await axios.delete(`${API}/api/admin/payments/rules/${ruleId}`);
      toast.success('Reël verwyder');
      fetchData();
    } catch (error) {
      toast.error('Kon nie reël verwyder nie');
    }
  };

  const resetRuleForm = () => {
    setRuleType('threshold');
    setThresholdAmount('');
    setMonthlyAmount('');
    setMonthlyDay('1');
    setRuleStartDate('');
    setRuleEndDate('');
  };

  const handleBankSelect = (bankName) => {
    setBankForm({
      ...bankForm,
      bank_name: bankName,
      branch_code: banks[bankName] || ''
    });
  };

  const handleSaveBankDetails = async () => {
    if (!selectedUser) return;
    
    setProcessing(true);
    try {
      const formData = new FormData();
      Object.entries(bankForm).forEach(([key, value]) => {
        if (value) formData.append(key, value);
      });
      
      await axios.put(`${API}/api/admin/users/${selectedUser.id}/bank-account`, formData);
      toast.success('Bankbesonderhede opgedateer');
      setShowBankDetails(false);
      setBankForm({
        bank_name: '',
        branch_code: '',
        account_number: '',
        account_holder_name: '',
        account_type: 'savings'
      });
      setSelectedUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kon nie bankbesonderhede opdateer nie');
    } finally {
      setProcessing(false);
    }
  };

  const openBankDetailsModal = async (user) => {
    setSelectedUser(user);
    // Fetch current bank details if they exist
    if (user.has_bank_account) {
      try {
        const response = await axios.get(`${API}/api/admin/users/${user.id}/bank-account`);
        if (response.data) {
          setBankForm({
            bank_name: response.data.bank_name || '',
            branch_code: response.data.branch_code || '',
            account_number: response.data.account_number || '',
            account_holder_name: response.data.account_holder_name || '',
            account_type: response.data.account_type || 'savings'
          });
        }
      } catch (error) {
        console.error('Could not fetch bank details');
      }
    } else {
      setBankForm({
        bank_name: '',
        branch_code: '',
        account_number: '',
        account_holder_name: user.name || '',
        account_type: 'savings'
      });
    }
    setShowBankDetails(true);
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      processing: { color: 'bg-blue-100 text-blue-800', icon: Clock },
      completed: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      failed: { color: 'bg-red-100 text-red-800', icon: XCircle },
      pending_manual: { color: 'bg-orange-100 text-orange-800', icon: AlertCircle }
    };
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>
        <Icon className="h-3 w-3" />
        {status}
      </span>
    );
  };

  const getRoleBadge = (role) => {
    const colors = {
      admin: 'bg-purple-100 text-purple-800',
      handler: 'bg-blue-100 text-blue-800',
      writer: 'bg-green-100 text-green-800',
      proofreader: 'bg-orange-100 text-orange-800',
      translator: 'bg-teal-100 text-teal-800'
    };
    const labels = {
      admin: 'Admin',
      handler: 'Hanteerder',
      writer: 'Skrywer',
      proofreader: 'Proefleser',
      translator: 'Vertaler'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[role] || 'bg-gray-100'}`}>
        {labels[role] || role}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div className="flex items-center gap-3">
          <Wallet className="h-8 w-8 text-orange-500" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Betalings Dashboard</h1>
            <p className="text-gray-600">Bestuur gebruiker betalings en reëls</p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Users className="h-8 w-8 text-blue-500" />
              <div>
                <p className="text-2xl font-bold">{users.length}</p>
                <p className="text-sm text-gray-500">Gebruikers</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <TrendingUp className="h-8 w-8 text-green-500" />
              <div>
                <p className="text-2xl font-bold">
                  R{users.reduce((sum, u) => sum + (u.balance?.current_balance || 0), 0).toFixed(2)}
                </p>
                <p className="text-sm text-gray-500">Totale Balanse</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Send className="h-8 w-8 text-orange-500" />
              <div>
                <p className="text-2xl font-bold">{transactions.length}</p>
                <p className="text-sm text-gray-500">Transaksies</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Clock className="h-8 w-8 text-purple-500" />
              <div>
                <p className="text-2xl font-bold">{rules.length}</p>
                <p className="text-sm text-gray-500">Aktiewe Reëls</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="users">Gebruikers & Balanse</TabsTrigger>
          <TabsTrigger value="transactions">Transaksie Geskiedenis</TabsTrigger>
          <TabsTrigger value="rules">Betalingsreëls</TabsTrigger>
        </TabsList>

        {/* Users Tab */}
        <TabsContent value="users" className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Soek gebruiker..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 w-full md:w-96"
              data-testid="user-search-input"
            />
          </div>

          <div className="space-y-3">
            {filteredUsers.map((user) => (
              <Card key={user.id} data-testid={`user-card-${user.id}`}>
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                        <span className="text-orange-600 font-medium">
                          {user.name?.charAt(0) || user.email?.charAt(0) || '?'}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium">{user.name || 'Geen naam'}</p>
                        <p className="text-sm text-gray-500">{user.email}</p>
                      </div>
                      {getRoleBadge(user.role)}
                      {user.is_handler && (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          Hanteerder
                        </span>
                      )}
                      {user.monthly_salary > 0 && (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          Salaris: R{user.monthly_salary.toLocaleString()}
                        </span>
                      )}
                      {!user.has_bank_account && (
                        <span className="text-xs text-red-500 flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          Geen bank
                        </span>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-6">
                      <div className="text-right">
                        <p className="text-lg font-bold text-green-600">
                          R{(user.balance?.current_balance || 0).toFixed(2)}
                        </p>
                        <p className="text-xs text-gray-500">Huidige Balans</p>
                      </div>
                      
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openBankDetailsModal(user)}
                          data-testid={`bank-btn-${user.id}`}
                          className={!user.has_bank_account ? 'border-red-300 text-red-600 hover:bg-red-50' : ''}
                        >
                          <CreditCard className="h-4 w-4 mr-1" />
                          {user.has_bank_account ? 'Bank' : 'Voeg Bank'}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedUser(user);
                            setShowAddBalance(true);
                          }}
                          data-testid={`add-balance-btn-${user.id}`}
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Balans
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => {
                            setSelectedUser(user);
                            setShowManualPayment(true);
                          }}
                          disabled={!user.has_bank_account || (user.balance?.current_balance || 0) <= 0}
                          className="bg-orange-500 hover:bg-orange-600"
                          data-testid={`pay-btn-${user.id}`}
                        >
                          <Send className="h-4 w-4 mr-1" />
                          Betaal
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedUser(user);
                            setShowCreateRule(true);
                          }}
                          disabled={!user.has_bank_account}
                          data-testid={`rule-btn-${user.id}`}
                        >
                          <Clock className="h-4 w-4 mr-1" />
                          Reël
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Transactions Tab */}
        <TabsContent value="transactions" className="space-y-4">
          {transactions.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <p className="text-gray-500">Geen transaksies nog nie</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {transactions.map((tx) => (
                <Card key={tx.id}>
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                          <Send className="h-5 w-5 text-gray-600" />
                        </div>
                        <div>
                          <p className="font-medium">{tx.user_name || 'Onbekend'}</p>
                          <p className="text-sm text-gray-500">{tx.user_email}</p>
                          <p className="text-xs text-gray-400">Ref: {tx.reference}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-6">
                        <div className="text-right">
                          <p className="text-lg font-bold">R{tx.amount.toFixed(2)}</p>
                          <p className="text-xs text-gray-500">{tx.payment_type}</p>
                        </div>
                        {getStatusBadge(tx.status)}
                        <p className="text-xs text-gray-400">
                          {new Date(tx.created_date).toLocaleDateString('af-ZA')}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Rules Tab */}
        <TabsContent value="rules" className="space-y-4">
          {rules.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <p className="text-gray-500">Geen aktiewe reëls nie</p>
                <p className="text-sm text-gray-400 mt-2">
                  Kies 'n gebruiker in die Gebruikers tab om 'n reël te skep
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {rules.map((rule) => (
                <Card key={rule.id}>
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          rule.rule_type === 'threshold' ? 'bg-blue-100' : 'bg-purple-100'
                        }`}>
                          {rule.rule_type === 'threshold' ? (
                            <TrendingUp className="h-5 w-5 text-blue-600" />
                          ) : (
                            <Calendar className="h-5 w-5 text-purple-600" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium">{rule.user_name}</p>
                          <p className="text-sm text-gray-500">{rule.user_email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-6">
                        <div className="text-right">
                          {rule.rule_type === 'threshold' ? (
                            <>
                              <p className="font-medium">Drempel: R{rule.threshold_amount}</p>
                              <p className="text-xs text-gray-500">Betaal wanneer balans drempel bereik</p>
                            </>
                          ) : (
                            <>
                              <p className="font-medium">Maandeliks: R{rule.monthly_amount}</p>
                              <p className="text-xs text-gray-500">Dag {rule.monthly_day} van elke maand</p>
                            </>
                          )}
                        </div>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDeleteRule(rule.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Manual Payment Dialog */}
      <Dialog open={showManualPayment} onOpenChange={setShowManualPayment}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Handmatige Betaling</DialogTitle>
            <DialogDescription>
              Maak 'n betaling aan {selectedUser?.name || selectedUser?.email}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="text-sm text-gray-500">Beskikbare Balans</p>
              <p className="text-xl font-bold text-green-600">
                R{(selectedUser?.balance?.current_balance || 0).toFixed(2)}
              </p>
            </div>
            <div className="space-y-2">
              <Label>Bedrag (ZAR)</Label>
              <Input
                type="number"
                step="0.01"
                placeholder="0.00"
                value={manualPaymentAmount}
                onChange={(e) => setManualPaymentAmount(e.target.value)}
                data-testid="manual-payment-amount"
              />
            </div>
            <div className="space-y-2">
              <Label>Notas (opsioneel)</Label>
              <Input
                placeholder="Betalingsnotas..."
                value={manualPaymentNotes}
                onChange={(e) => setManualPaymentNotes(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowManualPayment(false)}>
              Kanselleer
            </Button>
            <Button
              onClick={handleManualPayment}
              disabled={processing || !manualPaymentAmount || parseFloat(manualPaymentAmount) <= 0}
              className="bg-orange-500 hover:bg-orange-600"
              data-testid="confirm-payment-btn"
            >
              {processing ? 'Verwerk...' : 'Betaal Nou'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Balance Dialog */}
      <Dialog open={showAddBalance} onOpenChange={setShowAddBalance}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Voeg Balans By</DialogTitle>
            <DialogDescription>
              Voeg krediet by {selectedUser?.name || selectedUser?.email} se rekening
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Bedrag (ZAR)</Label>
              <Input
                type="number"
                step="0.01"
                placeholder="0.00"
                value={addBalanceAmount}
                onChange={(e) => setAddBalanceAmount(e.target.value)}
                data-testid="add-balance-amount"
              />
            </div>
            <div className="space-y-2">
              <Label>Beskrywing</Label>
              <Input
                placeholder="bv. Januarie 2024 Tantieme"
                value={addBalanceDescription}
                onChange={(e) => setAddBalanceDescription(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddBalance(false)}>
              Kanselleer
            </Button>
            <Button
              onClick={handleAddBalance}
              disabled={processing || !addBalanceAmount || parseFloat(addBalanceAmount) <= 0}
              className="bg-green-500 hover:bg-green-600"
              data-testid="confirm-add-balance-btn"
            >
              {processing ? 'Verwerk...' : 'Voeg By'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Rule Dialog */}
      <Dialog open={showCreateRule} onOpenChange={setShowCreateRule}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Skep Betalingsreël</DialogTitle>
            <DialogDescription>
              Stel outomatiese betalings op vir {selectedUser?.name || selectedUser?.email}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Reël Tipe</Label>
              <Select value={ruleType} onValueChange={setRuleType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="threshold">Drempel (betaal wanneer minimum bereik)</SelectItem>
                  <SelectItem value="monthly">Maandeliks (betaal elke maand)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {ruleType === 'threshold' ? (
              <div className="space-y-2">
                <Label>Drempel Bedrag (ZAR)</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="100.00"
                  value={thresholdAmount}
                  onChange={(e) => setThresholdAmount(e.target.value)}
                />
                <p className="text-xs text-gray-500">
                  Betaling word outomaties gemaak wanneer balans hierdie bedrag bereik
                </p>
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  <Label>Maandelikse Bedrag (ZAR)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="500.00"
                    value={monthlyAmount}
                    onChange={(e) => setMonthlyAmount(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Dag van Maand</Label>
                  <Select value={monthlyDay} onValueChange={setMonthlyDay}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[...Array(28)].map((_, i) => (
                        <SelectItem key={i + 1} value={String(i + 1)}>
                          Dag {i + 1}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Begin Datum (opsioneel)</Label>
                    <Input
                      type="date"
                      value={ruleStartDate}
                      onChange={(e) => setRuleStartDate(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Eindig Datum (opsioneel)</Label>
                    <Input
                      type="date"
                      value={ruleEndDate}
                      onChange={(e) => setRuleEndDate(e.target.value)}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreateRule(false); resetRuleForm(); }}>
              Kanselleer
            </Button>
            <Button
              onClick={handleCreateRule}
              disabled={processing || (ruleType === 'threshold' ? !thresholdAmount : !monthlyAmount)}
              className="bg-purple-500 hover:bg-purple-600"
            >
              {processing ? 'Skep...' : 'Skep Reël'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bank Details Dialog */}
      <Dialog open={showBankDetails} onOpenChange={setShowBankDetails}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Bankbesonderhede</DialogTitle>
            <DialogDescription>
              {selectedUser?.has_bank_account ? 'Wysig' : 'Voeg'} bankbesonderhede vir {selectedUser?.name || selectedUser?.email}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="admin_bank_name">Bank</Label>
              <Select value={bankForm.bank_name} onValueChange={handleBankSelect}>
                <SelectTrigger data-testid="admin-bank-select">
                  <SelectValue placeholder="Kies bank" />
                </SelectTrigger>
                <SelectContent>
                  {Object.keys(banks).map((bank) => (
                    <SelectItem key={bank} value={bank}>{bank}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="admin_branch_code">Takkode</Label>
                <Input
                  id="admin_branch_code"
                  value={bankForm.branch_code}
                  onChange={(e) => setBankForm({...bankForm, branch_code: e.target.value})}
                  placeholder="bv. 632005"
                  data-testid="admin-branch-code"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="admin_account_type">Rekening Tipe</Label>
                <Select 
                  value={bankForm.account_type} 
                  onValueChange={(v) => setBankForm({...bankForm, account_type: v})}
                >
                  <SelectTrigger data-testid="admin-account-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="savings">Spaarrekening</SelectItem>
                    <SelectItem value="cheque">Tjekrekening</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="admin_account_number">Rekeningnommer</Label>
              <Input
                id="admin_account_number"
                value={bankForm.account_number}
                onChange={(e) => setBankForm({...bankForm, account_number: e.target.value})}
                placeholder="Rekeningnommer"
                data-testid="admin-account-number"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="admin_account_holder_name">Rekeninghouer Naam</Label>
              <Input
                id="admin_account_holder_name"
                value={bankForm.account_holder_name}
                onChange={(e) => setBankForm({...bankForm, account_holder_name: e.target.value})}
                placeholder="Naam soos op bankrekening"
                data-testid="admin-account-holder-name"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBankDetails(false)}>
              Kanselleer
            </Button>
            <Button
              onClick={handleSaveBankDetails}
              disabled={processing || !bankForm.bank_name || !bankForm.account_number || !bankForm.account_holder_name}
              className="bg-blue-500 hover:bg-blue-600"
              data-testid="save-bank-details-btn"
            >
              {processing ? 'Stoor...' : 'Stoor Bankbesonderhede'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PaymentDashboard;
